import { lazy, Suspense, useState, type Ref } from "react";
import { Camera } from "lucide-react";
import clsx from "clsx";
import { Button } from "@/components/ui/Button";
import {
  canUseBarcodeCamera,
  prefersMobileBarcodeCamera,
} from "@/lib/barcodeCamera";

const BarcodeCameraScannerModal = lazy(() =>
  import("@/components/barcode/BarcodeCameraScannerModal").then((m) => ({
    default: m.BarcodeCameraScannerModal,
  })),
);

export interface EanInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Wird bei Enter oder Kamera-Scan aufgerufen; `code` ist der aktuelle Eingabewert. */
  onEnter?: (code: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  id?: string;
  inputRef?: Ref<HTMLInputElement>;
  /** Kamera-Button anzeigen (Standard: auf Mobil/Touch wenn Kamera verfügbar). */
  showCamera?: boolean;
  /** Kamera auch auf Desktop anzeigen. */
  cameraOnDesktop?: boolean;
}

export function EanInput({
  value,
  onChange,
  onEnter,
  disabled,
  placeholder = "Scannen oder eingeben…",
  className,
  inputClassName,
  id,
  inputRef,
  showCamera,
  cameraOnDesktop = false,
}: EanInputProps) {
  const [scannerOpen, setScannerOpen] = useState(false);

  const cameraAvailable = canUseBarcodeCamera();
  const showCameraButton =
    cameraAvailable &&
    (showCamera ??
      (cameraOnDesktop || prefersMobileBarcodeCamera()));

  function handleScan(code: string) {
    onChange(code);
    onEnter?.(code);
  }

  return (
    <>
      <div className={clsx("flex min-w-0 gap-2", className)}>
        <input
          id={id}
          ref={inputRef}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          className={clsx("eh-input min-w-0 flex-1 font-mono", inputClassName)}
          placeholder={placeholder}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onEnter?.(value);
            }
          }}
        />
        {showCameraButton ? (
          <Button
            type="button"
            variant="secondary"
            className="shrink-0 min-h-[44px] min-w-[44px] px-3"
            disabled={disabled}
            aria-label="Mit Kamera scannen"
            title="Mit Kamera scannen"
            onClick={() => setScannerOpen(true)}
          >
            <Camera className="h-5 w-5" aria-hidden />
          </Button>
        ) : null}
      </div>
      {scannerOpen ? (
        <Suspense fallback={null}>
          <BarcodeCameraScannerModal
            open={scannerOpen}
            onClose={() => setScannerOpen(false)}
            onScan={handleScan}
          />
        </Suspense>
      ) : null}
    </>
  );
}
